import { useNavigate } from "react-router-dom";

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <>
      <a
        href="#privacy-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-500 focus:text-background-50 focus:rounded-md focus:font-semibold focus:text-sm"
      >
        Skip to content
      </a>

      <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <nav className="flex items-center justify-between h-16 md:h-18">
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate("/"); }}
              className="flex items-center gap-2.5 select-none"
            >
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center">
                <span className="text-background-50 font-heading font-bold text-sm md:text-base">RS</span>
              </div>
              <span className="font-heading font-bold text-lg md:text-xl tracking-tight text-foreground-950">
                RedeemSoft
              </span>
            </a>
            <button
              onClick={() => navigate("/")}
              className="px-5 py-2.5 text-sm font-semibold rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Back to Home
            </button>
          </nav>
        </div>
      </header>

      <main id="privacy-content" className="pt-24 pb-16 md:pt-28 md:pb-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-4xl mx-auto">
          <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-foreground-500 mb-10">
            Last updated: January 15, 2026
          </p>

          <div className="space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed">
            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">1. Introduction</h2>
              <p>
                RedeemSoft, LLC (&ldquo;RedeemSoft,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
                respects the privacy of our clients, candidates, website visitors, and business partners. This Privacy
                Policy describes how we collect, use, store, and protect personal information obtained through our
                website (redeemsoft.com), our services, and our business operations.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">2. Information We Collect</h2>
              <p className="mb-2">We collect information necessary to deliver our technology solutions and IT recruitment services:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong>Client information:</strong> Business contact details, project requirements, system specifications, and contractual documentation provided in the course of our engagements.</li>
                <li><strong>Candidate information:</strong> Resumes, employment history, skills assessments, background verification results, compensation details, and professional references — collected only with the candidate&rsquo;s explicit consent.</li>
                <li><strong>Website information:</strong> Information submitted through our contact forms, newsletter signup, and job seeker registration, including name, email address, phone number, and message content.</li>
                <li><strong>Technical information:</strong> Standard web server logs including IP address, browser type, referring pages, and visit timestamps, used for security monitoring and site analytics.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">3. How We Use Information</h2>
              <p className="mb-2">We use collected information exclusively for legitimate business purposes:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Delivering technology solutions and IT recruitment services as contracted by our clients</li>
                <li>Matching qualified candidates with client requirements (with candidate consent)</li>
                <li>Responding to inquiries submitted through our website contact forms</li>
                <li>Complying with legal obligations, regulatory requirements, and contractual commitments</li>
                <li>Operating, maintaining, and improving the security of our website and systems</li>
              </ul>
              <p className="mt-2">We do not sell personal information to third parties. We do not use client or candidate data for purposes beyond the scope of our contractual engagement.</p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">4. Data Sharing and Disclosure</h2>
              <p className="mb-2">We share personal information only under the following circumstances:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong>With client organizations:</strong> Candidate information is shared with prospective employers only after obtaining the candidate&rsquo;s explicit consent for each specific opportunity.</li>
                <li><strong>Service providers:</strong> We engage third-party providers for background verification, skills assessment, and IT infrastructure — each bound by contractual data protection obligations.</li>
                <li><strong>Legal compliance:</strong> When required by law, regulation, legal process, or enforceable governmental request.</li>
                <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, subject to standard confidentiality arrangements.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">5. Data Security</h2>
              <p>
                We implement and maintain technical and organizational security measures appropriate to the sensitivity of
                the information we handle. These measures include encryption of data in transit and at rest, role-based
                access controls, multi-factor authentication, annual independent security assessments, and documented
                incident response procedures. Our security program is reviewed and updated on an ongoing basis to address
                evolving threats.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">6. Data Retention</h2>
              <p>
                We retain personal information for the duration of our business relationship and for a reasonable period
                thereafter to comply with legal obligations, resolve disputes, and enforce our agreements. Candidate
                information is retained for the duration of the candidate&rsquo;s active engagement with our recruitment
                services and for a period not exceeding three years following the last substantive interaction, unless a
                longer retention period is required by law.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">7. Your Rights</h2>
              <p className="mb-2">Depending on your jurisdiction, you may have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>The right to access and obtain a copy of your personal data</li>
                <li>The right to request correction of inaccurate or incomplete data</li>
                <li>The right to request deletion of your personal data, subject to legal retention requirements</li>
                <li>The right to restrict or object to certain processing activities</li>
                <li>The right to data portability where technically feasible</li>
              </ul>
              <p className="mt-2">
                To exercise these rights, please contact us at <a href="mailto:privacy@redeemsoft.com" className="text-primary-600 hover:text-primary-500 font-medium">privacy@redeemsoft.com</a>. We will respond to verified requests within 30 calendar days.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">8. International Data Transfers</h2>
              <p>
                RedeemSoft is headquartered in the United States. Personal information may be transferred to, stored, and
                processed in the United States or other countries where we or our service providers operate. For transfers
                of personal data from the European Economic Area, United Kingdom, or Switzerland, we rely on Standard
                Contractual Clauses and equivalent approved transfer mechanisms. Our Data Processing Agreement (DPA)
                incorporating these safeguards is available for client review at engagement initiation.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">9. Cookies and Tracking</h2>
              <p>
                Our website uses essential cookies required for site functionality and security. We do not use
                third-party tracking cookies, advertising cookies, or behavioral analytics cookies without prior notice
                and consent. Standard server logs are retained for a limited period for security monitoring purposes.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">10. Contact Us</h2>
              <p className="mb-2">For questions about this Privacy Policy or our data protection practices:</p>
              <div className="bg-background-100 rounded-lg p-5 border border-background-200/70 mt-2 space-y-1.5">
                <p><strong>RedeemSoft, LLC</strong></p>
                <p>100 Wood Avenue South, Suite 107</p>
                <p>Iselin, NJ 08830</p>
                <p>Email: <a href="mailto:privacy@redeemsoft.com" className="text-primary-600 hover:text-primary-500 font-medium">privacy@redeemsoft.com</a></p>
                <p>Phone: +1 (732) 548-8002</p>
              </div>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">11. Policy Updates</h2>
              <p>
                We may update this Privacy Policy periodically to reflect changes in our practices or legal obligations.
                Material changes will be communicated through our website or direct notification to affected individuals.
                Continued engagement with our services following an update constitutes acceptance of the revised policy.
              </p>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}